import * as React from "react";

import { api } from "@repo/backend";
import { Button } from "@repo/ui/components/button";
import { Calendar } from "@repo/ui/components/calendar";
import { ScrollArea } from "@repo/ui/components/scroll-area";
import { Skeleton } from "@repo/ui/components/skeleton";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { format, parseISO } from "date-fns";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { z } from "zod";

import { timezoneLabel } from "@/components/scheduling/scheduling-layout";
import { sessionTypeSchema } from "@/lib/scheduling";

export const Route = createFileRoute("/scheduling/calendar")({
  head: () => ({
    meta: [
      { title: "Select Date & Time | WorkGrid" },
      {
        name: "description",
        content:
          "Pick an available date and time for your staff photo session. Choose the slot that works best for you and confirm your booking.",
      },
    ],
  }),
  validateSearch: z.object({ type: sessionTypeSchema }),
  component: CalendarPage,
});

const asCalendarDate = (value: string) => parseISO(`${value}T12:00:00`);

function CalendarPage() {
  const { type } = Route.useSearch();
  const schedule = useQuery(api.scheduling.getPublicSchedule, { type });
  const navigate = useNavigate({ from: Route.fullPath });
  const [selectedDate, setSelectedDate] = React.useState<string>();
  const [hasMoreTimes, setHasMoreTimes] = React.useState(false);
  const timesViewportRef = React.useRef<HTMLDivElement>(null);
  const updateTimesOverflow = React.useCallback(() => {
    const viewport = timesViewportRef.current;
    if (!viewport) return;
    setHasMoreTimes(viewport.scrollTop + viewport.clientHeight < viewport.scrollHeight - 2);
  }, []);
  const availableDates = React.useMemo(
    () => new Set(schedule?.slots.map((slot) => slot.localDate) ?? []),
    [schedule],
  );
  const slots = React.useMemo(
    () => schedule?.slots.filter((slot) => slot.localDate === selectedDate) ?? [],
    [schedule, selectedDate],
  );

  if (schedule === undefined) return <CalendarSkeleton />;

  return (
    <section className="min-h-0 overflow-hidden bg-[#191919] p-5 sm:p-8">
      <h2 className="px-12 text-center text-lg font-semibold text-white italic">
        Select a date and time
      </h2>
      <div className="mt-4 grid min-h-[435px] gap-6 md:grid-cols-[minmax(280px,1fr)_minmax(220px,0.8fr)]">
        <Calendar
          mode="single"
          defaultMonth={asCalendarDate(schedule.config.startDate)}
          startMonth={asCalendarDate(schedule.config.startDate)}
          endMonth={asCalendarDate(schedule.config.endDate)}
          selected={selectedDate ? asCalendarDate(selectedDate) : undefined}
          onSelect={(date) => setSelectedDate(date ? format(date, "yyyy-MM-dd") : undefined)}
          disabled={(date) => !availableDates.has(format(date, "yyyy-MM-dd"))}
          weekStartsOn={1}
          className="mx-auto w-full bg-transparent p-0 text-white [--cell-size:--spacing(10)]"
          classNames={{
            root: "w-full",
            month: "w-full",
            today: "rounded-md bg-white/10 text-white",
            disabled: "text-white/20 opacity-100",
          }}
          showOutsideDays={false}
        />
        <div className="min-w-0 text-white">
          <div className="border-b border-white/10 pb-3">
            <p className="font-medium">
              {selectedDate ? format(asCalendarDate(selectedDate), "EEEE, MMMM d") : "Choose a day"}
            </p>
            <p className="mt-1 text-xs text-white/55">{timezoneLabel(schedule.config.timezone)}</p>
          </div>
          <div className="relative mt-3">
            <ScrollArea
              key={selectedDate}
              className="h-[370px] pr-4 [&_[data-slot=scroll-area-scrollbar]]:w-2 [&_[data-slot=scroll-area-scrollbar]]:rounded-full [&_[data-slot=scroll-area-scrollbar]]:bg-white/10 [&_[data-slot=scroll-area-thumb]]:bg-white/55 [&_[data-slot=scroll-area-thumb]]:hover:bg-white/70"
              viewportRef={(viewport) => {
                timesViewportRef.current = viewport;
                requestAnimationFrame(updateTimesOverflow);
              }}
              viewportProps={{ onScroll: updateTimesOverflow }}
            >
              <div className="space-y-2 pb-3">
                {!selectedDate ? (
                  <p className="py-10 text-center text-sm text-white/55">
                    Select an available date to see times.
                  </p>
                ) : slots.length ? (
                  slots.map((slot) => (
                    <Button
                      key={slot.startAt}
                      variant="outline"
                      className="h-11 w-full border-white/15 bg-white/[0.04] font-normal text-white hover:bg-white/10"
                      onClick={() =>
                        navigate({
                          to: "/scheduling/confirm",
                          search: { type, start: slot.startAt },
                        })
                      }
                    >
                      {slot.localStartTime}
                    </Button>
                  ))
                ) : (
                  <p className="py-10 text-center text-sm text-white/55">
                    No times are available on this day.
                  </p>
                )}
              </div>
            </ScrollArea>
            {hasMoreTimes ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-16 items-end justify-center bg-linear-to-t from-[#191919] via-[#191919]/85 to-transparent pb-1">
                <span className="flex items-center gap-1 text-xs font-medium text-white/80">
                  Scroll for more times <ChevronDownIcon className="size-3.5" />
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function CalendarSkeleton() {
  return (
    <section className="min-h-0 overflow-hidden bg-[#191919] p-5 sm:p-8">
      <h2 className="px-12 text-center text-lg font-semibold text-white italic">
        Select a date and time
      </h2>
      <div className="mt-4 grid min-h-[435px] gap-6 md:grid-cols-[minmax(280px,1fr)_minmax(220px,0.8fr)]">
        <div className="mx-auto w-full text-white">
          <div className="flex h-10 items-center justify-between px-1">
            <ChevronLeftIcon className="size-4 text-white/45" />
            <span className="text-sm font-medium">August 2026</span>
            <ChevronRightIcon className="size-4 text-white/45" />
          </div>
          <div className="flex">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((day) => (
              <span
                key={day}
                className="flex flex-1 items-center justify-center text-[0.8rem] text-white/55"
              >
                {day}
              </span>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-7">
            {Array.from({ length: 42 }, (_, index) => (
              <div key={index} className="flex aspect-square items-center justify-center p-1.5">
                <Skeleton className="size-full rounded-md bg-white/[0.06]" />
              </div>
            ))}
          </div>
        </div>
        <div className="min-w-0 text-white">
          <div className="border-b border-white/10 pb-3">
            <p className="font-medium">Choose a day</p>
            <p className="mt-1 text-xs text-white/55">Europe/Paris</p>
          </div>
          <p className="py-10 text-center text-sm text-white/55">
            Select an available date to see times.
          </p>
        </div>
      </div>
    </section>
  );
}
